import React from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  VStack,
  useDisclosure,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  MdOutlineOpenInFull,
  MdOutlinePhotoCamera,
  MdUploadFile,
  MdVideocam,
} from 'react-icons/md';
import Surface from 'components/ui/Surface';

const requiredSlots = [
  { key: 'front', label: 'Front Image' },
  { key: 'left', label: 'Left Side Image' },
  { key: 'right', label: 'Right Side Image' },
  { key: 'back', label: 'Back Image' },
];

const minimumAdditionalImages = 4;

const createInitialMandatoryState = (inspection) => ({
  front: inspection?.images?.find((image) => image.type === 'front') || null,
  left: inspection?.images?.find((image) => image.type === 'left') || null,
  right: inspection?.images?.find((image) => image.type === 'right') || null,
  back: inspection?.images?.find((image) => image.type === 'back') || null,
});

const getFilePreview = (file) => URL.createObjectURL(file);
const REAR_CAMERA_LABEL_PATTERN = /(back|rear|environment|world)/i;
const isVideoFileName = (value = '') =>
  /\.(mp4|webm|mov|m4v|quicktime)$/i.test(String(value));
const isImageFileName = (value = '') =>
  /\.(jpe?g|png|webp|heic|heif)$/i.test(String(value));
const isVideoMimeType = (mimeType, fileName = '') =>
  Boolean(
    (mimeType && mimeType.startsWith('video/')) || isVideoFileName(fileName),
  );
const isImageMimeType = (mimeType, fileName = '') =>
  Boolean(
    (mimeType && mimeType.startsWith('image/')) ||
    (!mimeType?.startsWith('video/') &&
      !isVideoFileName(fileName) &&
      isImageFileName(fileName)),
  );

const getBestVideoMimeType = () => {
  const preferredTypes = [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
    'video/mp4',
  ];

  return (
    preferredTypes.find(
      (mimeType) =>
        typeof MediaRecorder !== 'undefined' &&
        MediaRecorder.isTypeSupported(mimeType),
    ) || ''
  );
};

async function getRearCameraStream() {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Camera access is not supported in this browser.');
  }

  try {
    return await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { exact: 'environment' } },
      audio: false,
    });
  } catch (exactFacingError) {
    const initialStream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' } },
      audio: false,
    });

    const initialTrack = initialStream.getVideoTracks()[0];
    const initialSettings = initialTrack?.getSettings?.() || {};
    const initialLabel = initialTrack?.label || '';
    const looksRearFacing =
      initialSettings.facingMode === 'environment' ||
      REAR_CAMERA_LABEL_PATTERN.test(initialLabel);

    if (looksRearFacing) {
      return initialStream;
    }

    initialStream.getTracks().forEach((track) => track.stop());

    const devices = await navigator.mediaDevices.enumerateDevices();
    const rearDevice = devices.find(
      (device) =>
        device.kind === 'videoinput' &&
        REAR_CAMERA_LABEL_PATTERN.test(device.label || ''),
    );

    if (rearDevice?.deviceId) {
      return navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: rearDevice.deviceId } },
        audio: false,
      });
    }

    throw new Error('Rear camera is unavailable on this device.');
  }
}

function CameraCaptureModal({ isOpen, onClose, onCapture, mode = 'photo' }) {
  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const recorderRef = React.useRef(null);
  const shouldCaptureRef = React.useRef(false);
  const mediaChunksRef = React.useRef([]);
  const recordingTimerRef = React.useRef(null);
  const recordingSecondsRef = React.useRef(0);
  const [isStarting, setIsStarting] = React.useState(false);
  const [isRecording, setIsRecording] = React.useState(false);
  const [recordingSeconds, setRecordingSeconds] = React.useState(0);
  const [error, setError] = React.useState('');
  const toast = useToast();
  const modalBg = useColorModeValue('white', 'navy.800');

  const formatRecordingTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, '0');
    const seconds = Math.floor(totalSeconds % 60)
      .toString()
      .padStart(2, '0');

    return `${minutes}:${seconds}`;
  };

  const clearRecordingTimer = () => {
    if (recordingTimerRef.current) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  React.useEffect(() => {
    let active = true;

    const startCamera = async () => {
      if (!isOpen) return;
      setIsStarting(true);
      setError('');

      try {
        const stream = await getRearCameraStream();

        if (!active) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (captureError) {
        const message = captureError?.message || 'Unable to open rear camera.';
        setError(message);
        toast({
          title: 'Camera unavailable',
          description: message,
          status: 'error',
          borderRadius: '18px',
        });
      } finally {
        if (active) {
          setIsStarting(false);
        }
      }
    };

    startCamera();

    return () => {
      active = false;
      shouldCaptureRef.current = false;
      clearRecordingTimer();
      setIsRecording(false);
      setRecordingSeconds(0);
      recordingSecondsRef.current = 0;
      if (recorderRef.current?.state === 'recording') {
        recorderRef.current.stop();
      }
      recorderRef.current = null;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, [isOpen, toast, mode]);

  const cleanupStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const handleClose = () => {
    shouldCaptureRef.current = false;
    clearRecordingTimer();
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    } else {
      cleanupStream();
      recorderRef.current = null;
    }
    setIsRecording(false);
    setRecordingSeconds(0);
    recordingSecondsRef.current = 0;
    onClose();
  };

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;

    const context = canvas.getContext('2d');
    if (!context) {
      setError('Could not prepare camera snapshot.');
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError('Could not capture image.');
          return;
        }

        const file = new File([blob], `camera-${Date.now()}.jpg`, {
          type: 'image/jpeg',
        });
        onCapture(file);
        handleClose();
      },
      'image/jpeg',
      0.92,
    );
  };

  const handleStartRecording = () => {
    const stream = streamRef.current;
    if (!stream) return;

    const mimeType = getBestVideoMimeType();
    if (!mimeType) {
      setError('Video recording is not supported in this browser.');
      return;
    }

    try {
      mediaChunksRef.current = [];
      shouldCaptureRef.current = true;
      const recorder = new MediaRecorder(stream, { mimeType });

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          mediaChunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        setError('Unable to record video.');
        shouldCaptureRef.current = false;
        clearRecordingTimer();
        setIsRecording(false);
        setRecordingSeconds(0);
        recordingSecondsRef.current = 0;
        recorderRef.current = null;
      };

      recorder.onstop = () => {
        clearRecordingTimer();
        const shouldSave = shouldCaptureRef.current;
        shouldCaptureRef.current = false;
        setIsRecording(false);
        const capturedSeconds = recordingSecondsRef.current;
        setRecordingSeconds(0);
        recordingSecondsRef.current = 0;

        if (!shouldSave) {
          cleanupStream();
          recorderRef.current = null;
          return;
        }

        const blob = new Blob(mediaChunksRef.current, { type: mimeType });
        mediaChunksRef.current = [];

        if (!blob.size) {
          setError('Could not capture video.');
          cleanupStream();
          recorderRef.current = null;
          return;
        }

        const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
        const file = new File([blob], `camera-${Date.now()}.${extension}`, {
          type: mimeType,
        });
        onCapture(file);
        if (capturedSeconds > 0) {
          toast({
            title: 'Video captured',
            description: `Recorded for ${formatRecordingTime(capturedSeconds)}.`,
            status: 'success',
            borderRadius: '18px',
          });
        }
        cleanupStream();
        recorderRef.current = null;
        onClose();
      };

      recorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      setRecordingSeconds(0);
      recordingSecondsRef.current = 0;
      clearRecordingTimer();
      recordingTimerRef.current = window.setInterval(() => {
        recordingSecondsRef.current += 1;
        setRecordingSeconds(recordingSecondsRef.current);
      }, 1000);
    } catch (recordingError) {
      shouldCaptureRef.current = false;
      setIsRecording(false);
      setRecordingSeconds(0);
      recordingSecondsRef.current = 0;
      clearRecordingTimer();
      setError(recordingError?.message || 'Unable to start video recording.');
    }
  };

  const handleStopRecording = () => {
    if (recorderRef.current?.state === 'recording') {
      clearRecordingTimer();
      recorderRef.current.stop();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="full"
      motionPreset="slideInBottom"
      blockScrollOnMount
    >
      <ModalOverlay />
      <ModalContent
        bg={modalBg}
        borderRadius="0"
        m="0"
        w="100vw"
        h="100vh"
        maxW="100vw"
        maxH="100vh"
        overflow="hidden"
      >
        <ModalHeader
          px={{ base: '18px', md: '28px' }}
          pt={{ base: '18px', md: '24px' }}
          pb="14px"
        >
          {mode === 'video'
            ? 'Record a rear-camera video'
            : 'Take a rear-camera photo'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody
          px={{ base: '18px', md: '28px' }}
          pb={{ base: '18px', md: '24px' }}
          pt="0"
          display="flex"
          flexDirection="column"
          gap="16px"
          minH="0"
        >
          <Box
            flex="1"
            borderRadius="20px"
            overflow="hidden"
            bg="black"
            minH="0"
            position="relative"
          >
            {isStarting && (
              <HStack
                position="absolute"
                inset="0"
                justify="center"
                bg="rgba(0,0,0,0.35)"
                zIndex="1"
                spacing="10px"
              >
                <Spinner color="white" />
                <Text color="white" fontWeight="700">
                  Opening rear camera...
                </Text>
              </HStack>
            )}
            <Box
              as="video"
              ref={videoRef}
              autoPlay
              playsInline
              muted
              w="100%"
              h="100%"
              objectFit="cover"
            />
            {mode === 'video' && isRecording ? (
              <HStack
                position="absolute"
                top="16px"
                left="16px"
                px="12px"
                py="8px"
                borderRadius="999px"
                bg="rgba(220, 38, 38, 0.82)"
                color="white"
                spacing="8px"
              >
                <Box w="10px" h="10px" borderRadius="full" bg="white" />
                <Text fontWeight="700" fontSize="sm">
                  Recording {formatRecordingTime(recordingSeconds)}
                </Text>
              </HStack>
            ) : null}
          </Box>
          {error && (
            <Alert status="error" borderRadius="16px">
              <AlertIcon />
              {error}
            </Alert>
          )}
          <ModalFooter gap="10px" px="0" pb="0" pt="4px">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            {mode === 'video' ? (
              <Button
                variant="brand"
                onClick={
                  isRecording ? handleStopRecording : handleStartRecording
                }
                isDisabled={isStarting || Boolean(error)}
                colorScheme={isRecording ? 'red' : 'brand'}
              >
                {isRecording ? 'Stop recording' : 'Start recording'}
              </Button>
            ) : (
              <Button
                variant="brand"
                onClick={handleCapture}
                isDisabled={isStarting || Boolean(error)}
              >
                Capture photo
              </Button>
            )}
          </ModalFooter>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function FileUploadButton({
  multiple = false,
  onSelect,
  ariaLabel,
  accept = 'image/*',
  icon,
  containerProps,
}) {
  const inputId = React.useId();

  const handleInputChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    onSelect(multiple ? files : files[0]);
    event.target.value = '';
  };

  return (
    <Box
      as="label"
      htmlFor={inputId}
      position="relative"
      w="46px"
      h="46px"
      borderRadius="16px"
      border="1px solid"
      display="flex"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
      cursor="pointer"
      {...containerProps}
    >
      <Box
        pointerEvents="none"
        color="white"
        zIndex="1"
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="100%"
        h="100%"
        lineHeight="0"
      >
        {icon}
      </Box>
      <Input
        id={inputId}
        type="file"
        accept={accept}
        multiple={multiple}
        display="none"
        aria-label={ariaLabel}
        onChange={handleInputChange}
      />
    </Box>
  );
}

function MandatoryUploadCard({
  slot,
  image,
  previewUrl,
  previewMimeType,
  onPreview,
  onFileChange,
  onCameraClick,
  onVideoClick,
  onClear,
  showRemoveButton = true,
}) {
  const borderColor = useColorModeValue(
    'rgba(162,176,210,0.18)',
    'rgba(255,255,255,0.08)',
  );
  const placeholderBg = useColorModeValue(
    'rgba(246,248,252,0.92)',
    'rgba(255,255,255,0.04)',
  );
  const mutedText = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
  const titleColor = useColorModeValue('navy.800', 'white');
  const iconBg = useColorModeValue(
    'rgba(14, 13, 13, 0.78)',
    'rgba(17, 25, 40, 0.72)',
  );
  const iconBorder = useColorModeValue(
    'rgba(36, 39, 44, 0.26)',
    'rgba(255,255,255,0.10)',
  );
  const isVideo = isVideoMimeType(
    previewMimeType || image?.mimeType,
    previewUrl ||
      image?.originalFileName ||
      image?.fileName ||
      image?.url ||
      '',
  );

  return (
    <GridItem>
      <Surface variant="muted" p="18px" h="100%">
        <VStack align="stretch" spacing="14px" h="100%">
          <HStack justify="space-between">
            <Text fontSize="sm" fontWeight="800" color={titleColor}>
              Upload {slot.label}
            </Text>
            <Badge
              px="10px"
              py="6px"
              borderRadius="999px"
              bg={previewUrl || image?.url ? 'green.100' : 'orange.100'}
              color={previewUrl || image?.url ? 'green.500' : 'orange.500'}
            >
              {previewUrl || image?.url ? 'Ready' : 'Required'}
            </Badge>
          </HStack>

          <Box
            borderRadius="22px"
            minH={{ base: '160px', md: '190px' }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg={placeholderBg}
            border="1px dashed"
            borderColor={borderColor}
            overflow="hidden"
          >
            {previewUrl || image?.url ? (
              isVideo ? (
                <Box
                  as="video"
                  src={previewUrl || image?.url}
                  controls
                  w="100%"
                  h={{ base: '160px', md: '190px' }}
                  objectFit="cover"
                  bg="black"
                />
              ) : (
                <Image
                  src={previewUrl || image?.url}
                  alt={slot.label}
                  objectFit="cover"
                  w="100%"
                  h={{ base: '160px', md: '190px' }}
                />
              )
            ) : (
              <VStack spacing="10px">
                <Icon
                  as={MdOutlinePhotoCamera}
                  boxSize="34px"
                  color="brand.400"
                />
                <Text color={mutedText} fontWeight="700" textAlign="center">
                  Upload {slot.label}
                </Text>
              </VStack>
            )}
          </Box>

          <Box
            mt="auto"
            pt="2px"
            display="grid"
            gridTemplateColumns="repeat(5, minmax(0, 1fr))"
            gap="10px"
            sx={{
              '@media screen and (min-width: 480px)': {
                gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
              },
              '@media screen and (min-width: 768px)': {
                gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
              },
            }}
          >
            {(previewUrl || image?.url) && (
              <IconButton
                aria-label="Preview media"
                icon={
                  <Icon as={MdOutlineOpenInFull} boxSize="4" color="white" />
                }
                variant="outline"
                bg={iconBg}
                borderColor={iconBorder}
                size="md"
                w="full"
                h="46px"
                borderRadius="16px"
                onClick={onPreview}
              />
            )}
            <IconButton
              aria-label={
                previewUrl || image?.url
                  ? 'Retake with rear camera'
                  : 'Use rear camera'
              }
              icon={
                <Icon as={MdOutlinePhotoCamera} boxSize="5" color="white" />
              }
              variant="outline"
              bg={iconBg}
              borderColor={iconBorder}
              size="md"
              w="full"
              h="46px"
              borderRadius="16px"
              onClick={onCameraClick}
            />
            <IconButton
              aria-label={
                previewUrl || image?.url
                  ? 'Retake with rear video'
                  : 'Record rear video'
              }
              icon={<Icon as={MdVideocam} boxSize="5" color="white" />}
              variant="outline"
              bg={iconBg}
              borderColor={iconBorder}
              size="md"
              w="full"
              h="46px"
              borderRadius="16px"
              onClick={onVideoClick}
            />
            <FileUploadButton
              ariaLabel={
                previewUrl || image?.url
                  ? 'Replace from gallery'
                  : 'Upload from gallery'
              }
              accept="image/*,video/*"
              onSelect={(file) => onFileChange(slot.key, file)}
              icon={<Icon as={MdUploadFile} boxSize="5" display="block" />}
              containerProps={{
                bg: iconBg,
                borderColor: iconBorder,
                w: 'full',
              }}
            />
            {showRemoveButton && (previewUrl || image?.url) && (
              <IconButton
                aria-label="Remove image"
                icon={
                  <Text fontSize="lg" fontWeight="700" lineHeight="1">
                    ×
                  </Text>
                }
                variant="outline"
                colorScheme="red"
                size="md"
                w="full"
                h="46px"
                borderRadius="16px"
                onClick={() => onClear(slot.key)}
              />
            )}
          </Box>
        </VStack>
      </Surface>
    </GridItem>
  );
}

function AdditionalMediaCard({ media, onPreview, onRemove }) {
  const nameColor = useColorModeValue('secondaryGray.700', 'white');
  const isVideo = isVideoMimeType(
    media.mimeType || media.file?.type,
    media.name || media.originalFileName || media.fileName || media.url || '',
  );

  return (
    <Surface variant="muted" p="0" overflow="hidden">
      {isVideo ? (
        <Box
          as="video"
          src={media.previewUrl || media.url}
          controls
          h="160px"
          w="100%"
          objectFit="cover"
          bg="black"
        />
      ) : (
        <Image
          src={media.previewUrl || media.url}
          alt="Additional"
          h="160px"
          w="100%"
          objectFit="cover"
        />
      )}
      <HStack justify="space-between" p="14px">
        <Text fontSize="sm" color={nameColor} noOfLines={1}>
          {media.name ||
            media.originalFileName ||
            media.fileName ||
            (isVideo ? 'Additional video' : 'Additional image')}
        </Text>
        <HStack spacing="8px">
          <IconButton
            aria-label="Preview media"
            icon={<Icon as={MdOutlineOpenInFull} boxSize="4" />}
            size="sm"
            variant="subtle"
            onClick={onPreview}
          />
          {/* <Button
            size="sm"
            variant="outline"
            colorScheme="red"
            onClick={onRemove}
          >
            Remove
          </Button> */}
          <IconButton
            aria-label="Remove image"
            icon={
              <Text fontSize="xl" fontWeight="700" lineHeight="1">
                ×
              </Text>
            }
            variant="outline"
            colorScheme="red"
            size="md"
            w="full"
            h="40px"
            borderRadius="16px"
            onClick={onRemove}
          />
        </HStack>
      </HStack>
    </Surface>
  );
}

export default function InspectionForm({
  mode = 'create',
  initialInspection,
  isSubmitting,
  errorMessage,
  onSubmit,
}) {
  const toast = useToast();
  const [vinNumber, setVinNumber] = React.useState(
    initialInspection?.vinNumber || '',
  );
  const [remarks, setRemarks] = React.useState(
    initialInspection?.remarks || '',
  );
  const [mandatoryImages, setMandatoryImages] = React.useState(() =>
    createInitialMandatoryState(initialInspection),
  );
  const [mandatoryFiles, setMandatoryFiles] = React.useState({});
  const [mandatoryPreviews, setMandatoryPreviews] = React.useState({});
  const [extraMedia, setExtraMedia] = React.useState(
    initialInspection?.images
      ?.filter((image) => image.type === 'extra')
      .map((image) => ({
        ...image,
        removeId:
          image.key || image.url || image.fileName || image.originalFileName,
        existing: true,
        kind: isVideoMimeType(
          image.mimeType,
          image.originalFileName || image.fileName || image.url || '',
        )
          ? 'video'
          : 'image',
      })) || [],
  );
  const [extraMediaFiles, setExtraMediaFiles] = React.useState([]);
  const [removedExtraImageKeys, setRemovedExtraImageKeys] = React.useState([]);
  const cameraDisclosure = useDisclosure();
  const [cameraTarget, setCameraTarget] = React.useState(null);
  const [previewMedia, setPreviewMedia] = React.useState(null);
  const mutedText = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
  const previewModalBg = useColorModeValue('white', 'navy.800');
  const actionIconBg = useColorModeValue(
    'rgba(14, 13, 13, 0.78)',
    'rgba(17, 25, 40, 0.72)',
  );
  const actionIconBorder = useColorModeValue(
    'rgba(36, 39, 44, 0.26)',
    'rgba(255,255,255,0.10)',
  );

  React.useEffect(() => {
    setVinNumber(initialInspection?.vinNumber || '');
    setRemarks(initialInspection?.remarks || '');
    setMandatoryImages(createInitialMandatoryState(initialInspection));
    setExtraMedia(
      initialInspection?.images
        ?.filter((image) => image.type === 'extra')
        .map((image) => ({
          ...image,
          removeId:
            image.key || image.url || image.fileName || image.originalFileName,
          existing: true,
          kind: isVideoMimeType(
            image.mimeType,
            image.originalFileName || image.fileName || image.url || '',
          )
            ? 'video'
            : 'image',
        })) || [],
    );
    setRemovedExtraImageKeys([]);
    setExtraMediaFiles([]);
  }, [initialInspection]);

  const handleMandatoryFileChange = (key, file) => {
    if (!file) return;
    setMandatoryFiles((current) => ({ ...current, [key]: file }));
    setMandatoryPreviews((current) => ({
      ...current,
      [key]: getFilePreview(file),
    }));
  };

  const openCameraForMandatory = (key) => {
    setCameraTarget({ type: 'mandatory', key, mode: 'photo' });
    cameraDisclosure.onOpen();
  };

  const openVideoForMandatory = (key) => {
    setCameraTarget({ type: 'mandatory', key, mode: 'video' });
    cameraDisclosure.onOpen();
  };

  const openCameraForAdditional = () => {
    setCameraTarget({ type: 'additional', mode: 'photo' });
    cameraDisclosure.onOpen();
  };

  const openVideoRecorderForAdditional = () => {
    setCameraTarget({ type: 'additional', mode: 'video' });
    cameraDisclosure.onOpen();
  };

  const handleClearMandatory = (key) => {
    setMandatoryFiles((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    setMandatoryPreviews((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    if (mode === 'create') {
      setMandatoryImages((current) => ({ ...current, [key]: null }));
    }
  };

  const handleAdditionalMedia = (files) => {
    const nextFiles = Array.from(files || []);
    const nextSupportedFiles = nextFiles.filter(
      (file) =>
        isImageMimeType(file.type, file.name) ||
        isVideoMimeType(file.type, file.name),
    );
    const nextMedia = nextSupportedFiles
      .filter(
        (file) =>
          isImageMimeType(file.type, file.name) ||
          isVideoMimeType(file.type, file.name),
      )
      .map((file) => ({
        previewUrl: getFilePreview(file),
        name: file.name,
        existing: false,
        file,
        mimeType: file.type,
        kind: isVideoMimeType(file.type, file.name) ? 'video' : 'image',
      }));

    setExtraMediaFiles((current) => [...current, ...nextSupportedFiles]);
    setExtraMedia((current) => [...current, ...nextMedia]);
  };

  const handleCaptureFromCamera = (file) => {
    if (!file) return;

    if (cameraTarget?.type === 'mandatory') {
      handleMandatoryFileChange(cameraTarget.key, file);
    } else if (cameraTarget?.type === 'additional') {
      handleAdditionalMedia([file]);
    }

    toast({
      title: isVideoMimeType(file.type) ? 'Video captured' : 'Photo captured',
      status: 'success',
      borderRadius: '18px',
    });
    setCameraTarget(null);
  };

  const handleCloseCamera = () => {
    cameraDisclosure.onClose();
    setCameraTarget(null);
  };

  const openPreview = (media, title) => {
    const resolvedMimeType = media.mimeType || media.file?.type || '';
    const resolvedFileName =
      media.name || media.originalFileName || media.fileName || media.url || '';
    setPreviewMedia({
      url: media.previewUrl || media.url,
      mimeType: resolvedMimeType,
      isVideo: isVideoMimeType(resolvedMimeType, resolvedFileName),
      title,
    });
  };

  const closePreview = () => {
    setPreviewMedia(null);
  };

  const handleRemoveExtraImage = (index) => {
    const selected = extraMedia[index];
    setExtraMedia((current) =>
      current.filter((_, itemIndex) => itemIndex !== index),
    );
    if (selected?.existing && selected?.removeId) {
      setRemovedExtraImageKeys((current) =>
        current.includes(selected.removeId)
          ? current
          : [...current, selected.removeId],
      );
    } else {
      setExtraMediaFiles((current) =>
        current.filter((file) => file !== selected.file),
      );
    }
  };

  const submitForm = (event) => {
    event.preventDefault();

    onSubmit({
      vinNumber,
      remarks,
      frontImageFile: mandatoryFiles.front,
      leftImageFile: mandatoryFiles.left,
      rightImageFile: mandatoryFiles.right,
      backImageFile: mandatoryFiles.back,
      extraMediaFiles,
      retainExtraImageKeys: extraMedia
        .filter((media) => media.existing && media.removeId)
        .map((media) => media.removeId),
      removeExtraImageKeys: removedExtraImageKeys,
    });
  };

  const missingMandatory =
    mode === 'create' &&
    requiredSlots.some(
      (slot) => !mandatoryFiles[slot.key] && !mandatoryImages[slot.key],
    );
  const hasMinimumAdditionalImages =
    mode !== 'create' || extraMedia.length >= minimumAdditionalImages;

  return (
    <Box as="form" onSubmit={submitForm}>
      <Stack spacing="28px">
        {errorMessage && (
          <Alert status="error" borderRadius="20px">
            <AlertIcon />
            {errorMessage}
          </Alert>
        )}

        <SimpleGrid columns={{ base: 1, xl: 2 }} gap="20px">
          <FormControl isRequired>
            <FormLabel fontWeight="800">VIN Number</FormLabel>
            <Input
              value={vinNumber}
              onChange={(event) =>
                setVinNumber(event.target.value.toUpperCase())
              }
              placeholder="Enter VIN number"
              variant="main"
              size="lg"
            />
          </FormControl>
          {initialInspection?.inspectionNumber ? (
            <FormControl isDisabled>
              <FormLabel fontWeight="800">Inspection Number</FormLabel>
              <Input
                value={initialInspection?.inspectionNumber}
                variant="main"
                size="lg"
              />
            </FormControl>
          ) : (
            <></>
          )}
        </SimpleGrid>

        <Box>
          <Text fontSize="2xl" fontWeight="800" mb="6px">
            Vehicle Required Photos
          </Text>
          <Text color={mutedText} mb="18px">
            Upload the front, left, right, and back views before saving the
            inspection.
          </Text>
          {missingMandatory && (
            <Alert status="warning" borderRadius="20px" mb="18px">
              <AlertIcon />
              All four required vehicle photos must be uploaded before submit.
            </Alert>
          )}
          <Grid
            templateColumns={{
              base: '1fr',
              md: 'repeat(2, 1fr)',
              xl: 'repeat(3, 1fr)',
              '2xl': 'repeat(4, 1fr)',
            }}
            gap="18px"
          >
            {requiredSlots.map((slot) => (
              <MandatoryUploadCard
                key={slot.key}
                slot={slot}
                image={mandatoryImages[slot.key]}
                previewUrl={mandatoryPreviews[slot.key]}
                previewMimeType={mandatoryFiles[slot.key]?.type}
                onPreview={() =>
                  openPreview(
                    {
                      previewUrl: mandatoryPreviews[slot.key],
                      url: mandatoryImages[slot.key]?.url,
                      mimeType: mandatoryFiles[slot.key]?.type,
                    },
                    slot.label,
                  )
                }
                onFileChange={handleMandatoryFileChange}
                onCameraClick={() => openCameraForMandatory(slot.key)}
                onVideoClick={() => openVideoForMandatory(slot.key)}
                onClear={handleClearMandatory}
                showRemoveButton={mode === 'create'}
              />
            ))}
          </Grid>
        </Box>

        <Box>
          <Text fontSize="2xl" fontWeight="800" mb="6px">
            Additional Media
          </Text>
          <Text color={mutedText} mb="18px">
            Upload at least {minimumAdditionalImages} additional media items
            before saving the inspection. Videos are welcome as supporting
            media.
          </Text>
          {!hasMinimumAdditionalImages && (
            <Alert status="warning" borderRadius="20px" mb="18px">
              <AlertIcon />
              At least {minimumAdditionalImages} additional media items are
              required before you can submit this inspection.
            </Alert>
          )}
          <Stack
            direction={{ base: 'column', md: 'row' }}
            mb="18px"
            justify="space-between"
            align={{ base: 'flex-start', md: 'center' }}
            spacing="12px"
          >
            <Text
              fontSize="xs"
              color={mutedText}
              textTransform="uppercase"
              letterSpacing="0.12em"
            >
              Camera / Video / Gallery
            </Text>
            <HStack
              spacing="10px"
              flexWrap="wrap"
              justify={{ base: 'flex-start', md: 'flex-end' }}
              w={{ base: 'full', md: 'auto' }}
            >
              <IconButton
                aria-label="Add photo from rear camera"
                icon={
                  <Icon as={MdOutlinePhotoCamera} boxSize="5" color="white" />
                }
                variant="outline"
                bg={actionIconBg}
                borderColor={actionIconBorder}
                size="md"
                w="46px"
                h="46px"
                borderRadius="16px"
                onClick={openCameraForAdditional}
              />
              <IconButton
                aria-label="Record video from rear camera"
                icon={<Icon as={MdVideocam} boxSize="5" color="white" />}
                variant="outline"
                bg={actionIconBg}
                borderColor={actionIconBorder}
                size="md"
                w="46px"
                h="46px"
                borderRadius="16px"
                onClick={openVideoRecorderForAdditional}
              />
              <FileUploadButton
                ariaLabel="Add photo or video from gallery"
                multiple
                accept="image/*,video/*"
                onSelect={handleAdditionalMedia}
                icon={<Icon as={MdUploadFile} boxSize="5" display="block" />}
                containerProps={{
                  bg: actionIconBg,
                  borderColor: actionIconBorder,
                }}
              />
            </HStack>
          </Stack>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3, '2xl': 4 }} gap="18px">
            {extraMedia.map((media, index) => (
              <AdditionalMediaCard
                key={media.key || media.previewUrl || index}
                media={media}
                onPreview={() =>
                  openPreview(
                    media,
                    media.name ||
                      media.originalFileName ||
                      media.fileName ||
                      (isVideoMimeType(
                        media.mimeType || media.file?.type,
                        media.name || media.originalFileName || media.fileName,
                      )
                        ? 'Additional video'
                        : 'Additional image'),
                  )
                }
                onRemove={() => handleRemoveExtraImage(index)}
              />
            ))}
          </SimpleGrid>
        </Box>

        <FormControl>
          <FormLabel fontWeight="800">Remarks</FormLabel>
          <Textarea
            value={remarks}
            onChange={(event) => setRemarks(event.target.value)}
            placeholder="Add optional remarks about this inspection"
            minH="180px"
            borderRadius="20px"
          />
        </FormControl>

        <HStack justify="flex-end">
          <Button
            type="submit"
            variant="brand"
            isLoading={isSubmitting}
            isDisabled={!hasMinimumAdditionalImages}
          >
            {mode === 'edit' ? 'Update inspection' : 'Create inspection'}
          </Button>
        </HStack>
      </Stack>
      <CameraCaptureModal
        isOpen={cameraDisclosure.isOpen}
        onClose={handleCloseCamera}
        onCapture={handleCaptureFromCamera}
        mode={cameraTarget?.mode || 'photo'}
      />

      <Modal
        isOpen={Boolean(previewMedia?.url)}
        onClose={closePreview}
        size="6xl"
        isCentered
      >
        <ModalOverlay bg="rgba(10, 14, 26, 0.78)" backdropFilter="blur(10px)" />
        <ModalContent
          bg={previewModalBg}
          borderRadius="24px"
          border="1px solid"
          borderColor="rgba(255,255,255,0.08)"
          overflow="hidden"
        >
          <ModalCloseButton />
          <ModalBody p={{ base: '18px', md: '24px' }}>
            <Stack spacing="14px">
              <Text fontWeight="800" fontSize="lg">
                {previewMedia?.title || 'Media preview'}
              </Text>
              {previewMedia?.url ? (
                previewMedia.isVideo ? (
                  <Box
                    as="video"
                    src={previewMedia.url}
                    controls
                    maxH="78vh"
                    w="100%"
                    borderRadius="18px"
                    bg="black"
                  />
                ) : (
                  <Image
                    src={previewMedia.url}
                    alt={previewMedia.title || 'Media preview'}
                    maxH="78vh"
                    w="100%"
                    objectFit="contain"
                    borderRadius="18px"
                  />
                )
              ) : null}
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
